import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';

import { AppDispatch, RootState } from 'redux/store';
import Header from 'components/Common/Header';
import Tab from 'components/Common/Tab';
import routes from 'constants/routes';
import Button from 'components/Common/Button';
import AnnouncementContainer from 'components/Announcemnet/AnnouncementContainer';
import AnnouncementGroupContainer from 'components/Announcemnet/AnnouncementGroupContainer';
import { fetchAnnouncements } from 'redux/slices/announcements/announcementsSlice';
import MuiLoader from 'components/Common/Loader';
import { fetchAnnouncementGroups } from 'redux/slices/announcements/announcementGroupSlice';
import { ANNOUNCEMENT_GROUPS, ANNOUNCEMENTS, AnnouncementsTabs } from 'types/announcements';
import { setAnnouncementsTab, updateTableState } from 'redux/slices/common/appSlice';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});

const Announcements: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageAnnouncements = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');
    const { currentTab, tableState, announcementList, announcementGroupsList } = useSelector((state: RootState) => ({
        currentTab: state.app.announcement?.currentTab,
        tableState: state.app.table,
        announcementList: state.announcement.announcementsList,
        announcementGroupsList: state.announcement.announcementGroups,
    }));

    const { data: announcements, loading: announcementListLoading } = announcementList || {};
    const { records: announcementGroups, loading: announcementGroupLoading } = announcementGroupsList || {};
    const { pageNumber = 0, pageSize = 20 } = tableState || {};

    useEffect(() => {
        if (!parishId) return;
        if (pageNumber !== 0) {
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parish switches. Do NOT add pageNumber: this effect writes pageNumber, so depending on it would reset the user to page 1 on every pagination click. See Community.tsx / Reports.tsx for the same guard.
    }, [parishId, dispatch]);

    // Announcements: server-paginated — refetch on every page/pageSize change. fetchAnnouncements
    // reads pageNumber/pageSize fresh from state.app.table inside the thunk rather than as an
    // argument here, so this effect can just always fetch without needing to guard against the
    // stale-page render right after the parish-switch reset above (its dispatch — already
    // applied to the store by the time this effect's thunk runs — lands one render later than
    // this closure's own pageNumber, but the thunk doesn't use this closure's value at all).
    useEffect(() => {
        if (!parishId || currentTab !== ANNOUNCEMENTS) return;
        dispatch(fetchAnnouncements({ parishId }));
    }, [dispatch, parishId, currentTab, pageNumber, pageSize]);

    // Announcement Groups is now genuinely server-paginated (fetchAnnouncementGroups reads
    // page/pageSize fresh from state.app.table), so this effect re-fires on both pageNumber and
    // pageSize — Next/Prev fetches the real next page, and changing rows-per-page re-requests.
    useEffect(() => {
        if (!parishId || currentTab !== ANNOUNCEMENT_GROUPS) return;
        dispatch(fetchAnnouncementGroups({ parishId }));
    }, [dispatch, parishId, currentTab, pageNumber, pageSize]);

    const getLoading = useMemo(() => {
        switch (currentTab) {
            case ANNOUNCEMENT_GROUPS:
                return announcementGroupLoading;
            default:
                return announcementListLoading;
        }
    }, [currentTab, announcementGroupLoading, announcementListLoading]);

    const ButtonNavigationHandler = useCallback(() => {
        const targetRoute = currentTab === ANNOUNCEMENTS ? routes.announcements.new : routes.announcements.createGroup;
        navigate(targetRoute);
    }, [currentTab, navigate]);

    const content = useMemo(() => {
        switch (currentTab) {
            case ANNOUNCEMENTS:
                return <AnnouncementContainer data={announcements} loading={getLoading} />;
            case ANNOUNCEMENT_GROUPS:
                return <AnnouncementGroupContainer data={announcementGroups} loading={getLoading} />;
            default:
                return null;
        }
    }, [currentTab, announcements, announcementGroups, getLoading]);

    const handleSetTab = useCallback(
        (tab: AnnouncementsTabs) => {
            dispatch(setAnnouncementsTab(tab));
            // Announcements and Announcement Groups are both server-paginated now and share
            // the global table-pagination slice (see StyledTable) — reset it on every switch
            // so a page-2 view of one doesn't leave the other requesting an out-of-range page.
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        },
        [dispatch, pageSize]
    );

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <MuiLoader absolute loading={getLoading} />
            <div style={{ padding: 22 }}>
                <Header />
            </div>
            <StyledActionContainer>
                <StyledInnerContainer>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 22,
                            flexDirection: 'row',
                            justifyContent: 'space-around',
                        }}
                    >
                        <Tab
                            tabs={[
                                { value: ANNOUNCEMENTS, label: 'Announcements' },
                                { value: ANNOUNCEMENT_GROUPS, label: 'Announcement Groups' },
                            ]}
                            tabValue={currentTab}
                            setTabValue={handleSetTab}
                        />
                    </div>
                    {canManageAnnouncements && (
                        <Button
                            onClick={ButtonNavigationHandler}
                            sx={{ m: 2, width: { xs: '50%', md: '25%' }, fontSize: { xs: 14, lg: 18 } }}
                            title={currentTab === ANNOUNCEMENTS ? 'Create Announcement' : 'Create new Group'}
                        />
                    )}
                    <div>{content}</div>
                </StyledInnerContainer>
            </StyledActionContainer>
        </StyledContainer>
    );
};

export default Announcements;
